/**
 * §7.4 Feature Comparison — 13-row × 3-column licence matrix.
 * Desktop: proper table.  Mobile: stacked cards per capability.
 */

import React from 'react'
import clsx from 'clsx'
import { Check, Minus, Sparkles } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { COMPARISON } from '../data'

const COLUMNS = [
  { id: 'starter',      label: 'Starter',       accent: 'zinc'   },
  { id: 'professional', label: 'Professional',  accent: 'orange' },
  { id: 'enterprise',   label: 'Enterprise',    accent: 'green'  },
]

function Cell({ has, accent }) {
  if (!has) {
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-zinc-600">
        <Minus className="h-3 w-3" />
      </span>
    )
  }
  return (
    <span className={clsx(
      'inline-flex h-6 w-6 items-center justify-center rounded-full border',
      accent === 'orange' && 'border-brand-gold/40 bg-brand-gold/15 text-brand-gold',
      accent === 'green'  && 'border-emerald-400/40 bg-emerald-400/15 text-emerald-400',
      accent === 'zinc'   && 'border-white/15 bg-white/[0.06] text-white',
    )}>
      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
    </span>
  )
}

export default function Comparison() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>License Comparison</Eyebrow>
          <SectionHeading align="center"
            title={<>Same platform,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">different service levels.</span></>}
            subtitle="Every licence unlocks the entire platform. Higher tiers add priority deployment, priority support, and dedicated success management."
          />
        </div>

        {/* DESKTOP TABLE */}
        <div className="hidden md:block relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-6 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.08), transparent 70%)' }} />

          {/* header */}
          <div className="relative grid grid-cols-12 gap-4 items-center px-3 pb-3 mb-2 border-b border-white/5">
            <div className="col-span-6 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Capability</div>
            {COLUMNS.map((c) => (
              <div key={c.id} className={clsx(
                'col-span-2 text-[10.5px] font-mono uppercase tracking-wider text-center',
                c.accent === 'orange' && 'text-brand-gold',
                c.accent === 'green'  && 'text-emerald-400',
                c.accent === 'zinc'   && 'text-zinc-400',
              )}>{c.label}</div>
            ))}
          </div>

          <div className="relative flex flex-col gap-1">
            {COMPARISON.map((r, idx) => (
              <div key={r.id} className={clsx(
                'grid grid-cols-12 gap-4 items-center rounded-xl px-3 py-2.5',
                idx % 2 === 0 ? 'bg-white/[0.015]' : 'bg-transparent',
                'hover:bg-white/[0.03] transition-colors',
              )}>
                <div className="col-span-6 text-[13.5px] font-medium text-white">{r.capability}</div>
                <div className="col-span-2 flex items-center justify-center"><Cell has={r.starter}      accent="zinc"   /></div>
                <div className="col-span-2 flex items-center justify-center"><Cell has={r.professional} accent="orange" /></div>
                <div className="col-span-2 flex items-center justify-center"><Cell has={r.enterprise}   accent="green"  /></div>
              </div>
            ))}
          </div>

          {/* footnote */}
          <div className="relative mt-4 flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
            <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
            <p className="text-[12.5px] text-zinc-300">
              <span className="font-semibold text-white">Instead of removing platform features,</span>{' '}
              higher tiers differentiate through service level — deployment priority, support SLA and dedicated success.
            </p>
          </div>
        </div>

        {/* MOBILE stacked cards */}
        <div className="md:hidden flex flex-col gap-3">
          {COMPARISON.map((r) => (
            <div key={r.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[14px] font-semibold text-white mb-2">{r.capability}</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { l: 'Starter', h: r.starter,      a: 'zinc'   },
                  { l: 'Pro',      h: r.professional, a: 'orange' },
                  { l: 'Ent',      h: r.enterprise,   a: 'green'  },
                ].map((c) => (
                  <div key={c.l} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                    <span className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-400">{c.l}</span>
                    <Cell has={c.h} accent={c.a} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}
